import { ChangeEvent, useState, useEffect } from 'react';
import deepEqual from 'deep-equal';
import { Button } from '@upmc/ims.component-library';

import axiosInstance from '../../services/axios/axios';
import Edit from './containers/Edit';
import FormEntry from './components/FormEntry';
import SubmissionSummary from './containers/SubmissionSummary';
import UARLayout from './containers/Layout';
import {
	TextboxTypes,
	DropdownTypes,
} from '../../components/UI/Input';
import { Headline6 } from '../../components/UI/MDC/Typography/Typography';
import { WrapImsPacket } from '../../store/utility';
import * as utils from '../../utils';
import {
	SUBMISSION_LOADING_BULK_MESSAGE,
	SUBMISSION_LOADING_TITLE,
} from './util/constants';
import Icon from '../../components/UI/Icon/Icon';

/**
 * User Access Review Delegate Component
 * @param {object} props
 */
const Delegate = props => {
	/**
	 * Extract needed variables from the props provided by the `Scaffold`
	 */
	const {
		loggedInUser,
		error,
		setError,
		errorMessage,
		setErrorMessage,
		loading,
		setLoading,
		userStaff,
		usersFound,
		targetProfileDetails,
		bulkSelections,
		bulkFlow,
		canPerformActions,
	} = props;

	/**
	 * (set)ShowHelp - display additional help information
	 */
	const [showHelp, setShowHelp] = useState(false);
	/**
	 * (set)Submitted - flag to indicate if submit button has been clicked
	 */
	const [submitted, setSubmitted] = useState(false);
	/**
	 * (set)SubmittedSuccessfully - flag to indicate if submission was successful
	 */
	const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);
	/**
	 * set(Form) - data for input form
	 */
	const [form, setForm] = useState({
		startDate: {
			label: 'Date to begin delegation',
			value: '',
			valueType: 'SINGLE',
			options: null,
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.DATE,
			},
		},
		endDate: {
			label: 'Date to cease delegation',
			value: '',
			valueType: 'SINGLE',
			options: null,
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.DATE,
			},
		},
		staffDelegatee: {
			label: 'Delegatee',
			value: [],
			valueType: 'SINGLE',
			options: [],
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					// minCount: 1
					/*
                    TODO|BPK - idea:  make rules: { requirementType: {criteria: [value], defaultMessage: ['message']}}
                    */
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: DropdownTypes.DROPDOWN,
			},
		},
	});

	//LIFECYCLE
	useEffect(() => {
		//Remove the staff to delegate from the list of delegatee options
		if (usersFound) {
			if (
				form.staffDelegatee.options &&
				form.staffDelegatee.options.length > 0
			) {
				if (bulkFlow) {
					let newForm = { ...form };
					let spliceIndices = [];
					if (newForm.staffDelegatee.options) {
						for (
							let i = 0;
							i < newForm.staffDelegatee.options.length;
							i++
						) {
							for (let j = 0; j < bulkSelections.length; j++) {
								if (
									newForm.staffDelegatee.options[i]
										.esoFiId === bulkSelections[j].esoFiId
								) {
									spliceIndices.push(i);
								}
							}
						}
					}
					spliceIndices = spliceIndices.reverse();
					spliceIndices.forEach(i => {
						newForm.staffDelegatee.options.splice(i, 1);
					});
					setForm(newForm);
					if (newForm.staffDelegatee.options.length === 0) {
						setError(true);
						setErrorMessage('No staff available to delegate to.');
					}
				} else if (targetProfileDetails) {
					let newForm = { ...form };
					let spliceIndex = -1;
					if (newForm.staffDelegatee.options) {
						for (
							let i = 0;
							i < newForm.staffDelegatee.options.length;
							i++
						) {
							if (
								newForm.staffDelegatee.options[i].esoFiId ===
								targetProfileDetails.esoFiId
							) {
								spliceIndex = i;
							}
						}
						if (spliceIndex > -1) {
							newForm.staffDelegatee.options.splice(
								spliceIndex,
								1
							);
						}
					}
					setForm(newForm);
				}
				setLoading(false);
				setError(false);
			} else {
				setLoading(true);
				setError(false);
			}
		} else {
			setError(true);
			setErrorMessage('No staff selected to delegate.');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form.staffDelegatee.options, usersFound]);

	useEffect(() => {
		if (userStaff) {
			const newForm = { ...form };
			newForm.staffDelegatee.options.push({
				key: '--select--',
				value: '--select--',
			});
			userStaff.staff.forEach(staff => {
				if (staff.typeId === 7) {
					newForm.staffDelegatee.options.push(staff);
				}
			});
			setForm(newForm);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userStaff]);

	//FUNCTIONS
	const arrayContainsObject = (arr, obj) => {
		for (let index in arr) {
			if (deepEqual(arr[index], obj)) {
				return index;
			}
		}
		return -1;
	};
	/**
	 * event handler for form input changes
	 * @param {ChangeEvent<HTMLInputElement>} event
	 * @param {string} key
	 */
	const handleChange = (event, key) => {
		if (key in form) {
			let newForm = { ...form };
			if (newForm[key].valueType === 'MULTI') {
				let foundIndex = arrayContainsObject(
					newForm[key].value,
					event.target.value
				);
				if (foundIndex > -1) {
					newForm[key].value.splice(foundIndex, 1);
				} else {
					newForm[key].value.push(event.target.value);
				}
			} else {
				newForm[key].value = event.target.value;
			}
			newForm[key].touched = true;

			setForm(newForm);
		}
	};

	/**
	 * actions to take upon form submission
	 */
	const submitHandler = () => {
		const isValid = validate();
		if (isValid) {
			let targets = [];
			if (bulkSelections && bulkSelections.length > 0) {
				targets = bulkSelections;
			} else {
				targets.push(targetProfileDetails);
			}

			const delegatee = form.staffDelegatee.options.filter(option => {
				return option.value === form.staffDelegatee.value;
			})[0];
			const payload = {
				loggedInUser: loggedInUser.loggedInUser,
				staff: targets, //TODO|BPK - switch on API
				startDate: form.startDate.value,
				endDate: form.endDate.value,
				delegatee: delegatee, //TODO|BPK - switch on API
			};

			setSubmitted(true);
			axiosInstance
				.post(
					'/api/UserAccessReview/delegation',
					WrapImsPacket(payload)
				)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								switch (responseCode) {
									case 200:
										setSubmittedSuccessfully(true);
										break;
									default:
										setSubmitted(false);
										setSubmittedSuccessfully(false);
										break;
								}
							}
							break;

						default:
							setSubmitted(false);
							setSubmittedSuccessfully(false);
							break;
					}
				})
				.catch(errors => {
					setSubmitted(false);
					setSubmittedSuccessfully(false);
				});
		}
		//TODO|BPK: else post message
	};

	/**
	 * actions to take if cancelled
	 */
	const cancelHandler = () => {
		props.history.goBack();
	};

	/**
	 * form validation logic
	 * @param {ChangeEvent<HTMLInputElement>} event
	 */
	const validate = event => {
		const touchedKey =
			event && event.target && event.target.id ? event.target.id : null;
		let newForm = { ...form };
		let allValid = true;
		for (let key in newForm) {
			let valid = false;
			let value = newForm[key].value;
			let message = '';
			switch (key) {
				case 'delegatee':
					valid = value ? true : false;
					if (!valid) {
						message = 'Please select a valid option.';
					}
					break;
				case 'startDate':
					if (newForm[key].visible) {
						// utils.prettyDateFormat('');
						const isEmpty = value ? false : true;
						const isDate = utils.validateDateString(value);
						const isFutureOrToday =
							isDate &&
							(utils.isDateStringInFuture(value) ||
								utils.isDateStringToday(value));
						valid = !isEmpty && isDate && isFutureOrToday;

						if (!isFutureOrToday) {
							message =
								"The date provided is in the past.  Please select today's date or a future date.";
						}
						if (!isDate) {
							message = 'The value provided is not a valid date.';
						}
						if (isEmpty) {
							message = 'Please provide a valid date.';
						}
					}
					break;
				case 'endDate':
					if (newForm[key].visible) {
						const isEmpty = value ? false : true;
						const isDate = utils.validateDateString(value);
						const isFutureOrToday =
							isDate &&
							(utils.isDateStringInFuture(value) ||
								utils.isDateStringToday(value));
						const isFutureFromStartDate = newForm['startDate'].value
							? isDate &&
							  (utils.isDateStringInFuture(value) ||
									utils.isDateStringGreaterThan(
										newForm['startDate'].value,
										value
									))
							: true;
						valid = !isEmpty && isDate && isFutureOrToday;

						if (!isFutureOrToday) {
							message =
								"The date provided is in the past.  Please select today's date or a future date.";
						}
						if (!isDate) {
							message = 'The value provided is not a valid date.';
						}
						if (isEmpty) {
							message = 'Please provide a valid date.';
						}
						if (!isFutureFromStartDate) {
							message =
								'The date provided is after the selected start date.';
						}
					}
					break;
				case 'staffDelegatee':
					if (value) {
						//value is an array of objects for `staffDelegatee`
						valid =
							newForm[key].options.filter(option => {
								return value === option.value;
							}).length > 0;
						valid = valid && value !== '--select--';

						if (!valid) {
							message = 'Please select a staff member.'; //TODO|BPK - select only one
						}
					} else {
						valid = true;
					}
					break;
				default:
					break;
			}
			newForm[key].validation.valid = valid;
			newForm[key].validation.message = message;

			//no `touchedKey` indicates a submission event
			if (!touchedKey) {
				newForm[key].validation.touched = true;
			}

			allValid = allValid && valid;
		}

		//presence of `touchedKey` indicates an input focus-change event
		if (touchedKey) {
			newForm[touchedKey].validation.touched = true;
		}

		setForm(newForm);
		return allValid;
	};

	//RENDER
	/**
	 * Retrieve settings from state and generate the input component
	 * @param {string} key
	 */
	const generateFormInput = key => {
		let formInput = null;
		if (key in form && form[key].visible) {
			formInput = (
				<FormEntry
					entryKey={key}
					formDetails={form[key]}
					handleChange={e => handleChange(e, key)}
					validateChange={validate}
					inputType={
						form[key].settings && form[key].settings.type
							? form[key].settings.type
							: 'text'
					}
					readOnly={
						(form[key].settings && form[key].settings.readOnly) ||
						submittedSuccessfully
					}
					placeholder={
						form[key].settings && form[key].settings.placeholder
							? form[key].settings.placeholder
							: null
					}
				/>
			);
		}
		return formInput;
	};

	/**
	 * Retrieve settings from state and generate the summary
	 * @param {string} key
	 */
	const generateFormSummary = key => {
		let formInput = null;
		if (key in form && form[key].visible) {
			formInput = (
				<FormEntry
					entryKey={key}
					formDetails={form[key]}
					handleChange={e => handleChange(e, key)}
					validateChange={validate}
					inputType={'SUMMARY'}
					readOnly={true}
					value={form[key].value}
				/>
			);
		}
		return formInput;
	};

	/**
	 * Form components
	 */
	const formOutput = (
		<>
			<Headline6>
				This page allows you to delegate annual user review process to
				your direct reports (employees).
			</Headline6>
			<Button
				onClick={() => setShowHelp(!showHelp)}
				variant='text'
				endIcon={
					<Icon
						icon={
							showHelp
								? 'unfold_less'
								: 'unfold_more'
						}
					/>
				}
			>
				{showHelp ? 'Read Less' : 'Read More'}
			</Button>
			{showHelp ? (
				<ul>
					<li>Select a date to start delegation.</li>
					<li>Select a date to stop delegation.</li>
					<li>A delegatee cannot be his or her own reviewer.</li>
					<li>
						Clicking <strong>Confirm Delegation</strong> will
						delegate the selected account to the selected delegatee.
					</li>
				</ul>
			) : null}
			{/* Delegatee */}
			{generateFormInput('delegatee')}

			{/* Start Date */}
			{generateFormInput('startDate')}

			{/* End Date */}
			{generateFormInput('endDate')}

			{/* staffDelegatee List */}
			{generateFormInput('staffDelegatee')}
		</>
	);

	/**
	 * Form summary components
	 */
	const formOutputSubmitted = (
		<>
			{/* Delegatee */}
			{generateFormSummary('delegatee')}

			{/* Start Date */}
			{generateFormSummary('startDate')}

			{/* End Date */}
			{generateFormSummary('endDate')}

			{/* staffDelegatee List */}
			{generateFormSummary('staffDelegatee')}
		</>
	);

	/**
	 * return JSX to render
	 */
	return (
		<UARLayout
			title='Delegate'
			loading={loading}
			error={error}
			errorMessage={errorMessage}
			canPerformActions={canPerformActions}
		>
			{submittedSuccessfully ? (
				<SubmissionSummary
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: targetProfileDetails
					}
				>
					{formOutputSubmitted}
				</SubmissionSummary>
			) : (
				<Edit
					cancellable={{
						handler: cancelHandler,
						disabled: submitted,
					}}
					submittable={{
						handler: submitHandler,
						label: 'Confirm Delegation',
						disabled: submitted,
					}}
					loading={{
						isLoading: submitted,
						title: SUBMISSION_LOADING_TITLE,
						message: bulkFlow
							? SUBMISSION_LOADING_BULK_MESSAGE
							: null,
					}}
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: targetProfileDetails
					}
				>
					{formOutput}
				</Edit>
			)}
		</UARLayout>
	);
};

export default Delegate;
